import { resolveAgent } from './registry.js';

/**
 * Estado central da economia observada: nós (agentes), arestas (pares
 * from->to agregados) e feed de pagamentos. Pub/sub simples via
 * EventTarget pra UI reagir sem framework.
 */
export class Store extends EventTarget {
  constructor(registry) {
    super();
    this.registry = registry;
    this.nodes = new Map(); // address -> node
    this.edges = new Map(); // "from|to" -> edge
    this.payments = []; // histórico completo, em ordem de idx
    this.seenTx = new Set(); // dedupe por hash
    this.lastPaymentAt = null;
    this.lastIdx = -1; // maior idx visto (retomada de polling/backfill)
    this.supply = null; // string ainda não decodificada em BigInt (ver /head)
  }

  _getOrCreateNode(address) {
    let node = this.nodes.get(address);
    if (!node) {
      const agent = resolveAgent(this.registry, address);
      node = {
        address,
        agent,
        totalSent: 0n,
        totalReceived: 0n,
        sentCount: 0,
        receivedCount: 0,
      };
      this.nodes.set(address, node);
    }
    return node;
  }

  _getOrCreateEdge(from, to) {
    const key = `${from}|${to}`;
    let edge = this.edges.get(key);
    if (!edge) {
      edge = { key, from, to, volume: 0n, count: 0 };
      this.edges.set(key, edge);
    }
    return edge;
  }

  /**
   * Adiciona um lote de txs já normalizadas pelo ledger.js (value: BigInt,
   * timestamp: ms). Ignora o gênesis, as que envolvem o deployer (tesouro
   * de bootstrap, não aparece no grafo) e os duplicados (por hash).
   * Retorna as efetivamente novas, em ordem de idx.
   */
  addPayments(rawPayments) {
    const added = [];

    for (const p of rawPayments) {
      if (p.type === 'genesis') continue;
      if (this.registry.deployerAddresses.has(p.from) || this.registry.deployerAddresses.has(p.to)) {
        continue;
      }
      if (this.seenTx.has(p.hash)) continue;
      this.seenTx.add(p.hash);

      const fromNode = this._getOrCreateNode(p.from);
      const toNode = this._getOrCreateNode(p.to);
      fromNode.totalSent += p.value;
      fromNode.sentCount++;
      toNode.totalReceived += p.value;
      toNode.receivedCount++;

      const edge = this._getOrCreateEdge(p.from, p.to);
      edge.volume += p.value;
      edge.count++;

      this.payments.push(p);
      added.push(p);
      if (!this.lastPaymentAt || (p.timestamp && p.timestamp > this.lastPaymentAt)) {
        this.lastPaymentAt = p.timestamp;
      }
      if (p.idx > this.lastIdx) this.lastIdx = p.idx;
    }

    if (added.length) {
      this.payments.sort((a, b) => a.idx - b.idx);
      this.dispatchEvent(new CustomEvent('payments', { detail: { added } }));
    }
    return added;
  }

  get stats() {
    return {
      totalPayments: this.payments.length,
      totalVolume: this.payments.reduce((acc, p) => acc + p.value, 0n),
      activeAgents: this.nodes.size,
      lastPaymentAt: this.lastPaymentAt,
      supply: this.supply,
    };
  }
}
